import React from 'react';
import styled from 'styled-components';
import BentoCard from './BentoCard';
import { Avatar } from '../../../Avatar';

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const Primary = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Secondary = styled.div`
  font-size: 11px;
  color: ${props => props.theme.colors.textSecondary};
`;

const Value = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.$positive ? '#10b981' : props.$negative ? '#ef4444' : props.theme.colors.textPrimary};
  white-space: nowrap;
`;

const ValueWrapper = styled.div`
  text-align: right;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

const EmptyState = styled.div`
  padding: 1.5rem;
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 13px;
`;

/**
 * ActivityList Component - BentoCard with standardized list items
 * 
 * @param {string} title - Card title
 * @param {string} action - Action link text
 * @param {string} actionHref - Action link URL
 * @param {Function} onAction - Action click handler
 * @param {Array} items - Array of items to display
 * @param {Function} renderItem - Function that returns item data: { avatar, avatarFallback, primary, secondary, value, valuePositive, valueNegative, extra }
 * @param {string} emptyMessage - Message when no items
 * @param {number} limit - Max items to show (default: 3)
 * @param {string} keyField - Field to use as key (default: 'id')
 * @param {boolean} showAvatar - Whether to show avatar (default: true)
 */
const ActivityList = ({
  title,
  action,
  actionHref,
  onAction,
  items = [],
  renderItem,
  emptyMessage = 'No items',
  limit = 3,
  keyField = 'id',
  showAvatar = true,
  className,
  style
}) => {
  const displayItems = items?.slice(0, limit) || [];
  
  return (
    <BentoCard 
      title={title} 
      action={action} 
      actionHref={actionHref}
      onAction={onAction}
      className={className}
      style={style}
    >
      {displayItems.length > 0 ? (
        <List>
          {displayItems.map((item, index) => {
            const data = renderItem(item, index);
            const key = item[keyField] || index;
            
            return (
              <ListItem key={key}>
                {showAvatar && (
                  <Avatar 
                    src={data.avatar} 
                    name={data.avatarFallback || data.primary}
                    size="32px"
                    hideOnlineIndicator
                  />
                )}
                <Content>
                  <Primary>{data.primary}</Primary>
                  {data.secondary && <Secondary>{data.secondary}</Secondary>}
                </Content>
                {(data.value || data.extra) && (
                  <ValueWrapper>
                    {data.value && (
                      <Value $positive={data.valuePositive} $negative={data.valueNegative}>
                        {data.value}
                      </Value>
                    )}
                    {data.extra}
                  </ValueWrapper>
                )}
              </ListItem>
            );
          })}
        </List>
      ) : (
        <EmptyState>{emptyMessage}</EmptyState>
      )}
    </BentoCard>
  );
};

export default ActivityList;
